import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { theme } from "../../styles/theme";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const SliderWrapper = styled.div`
  padding: ${theme.spacing.sm} 0;
`;

const TimeLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.sm};
`;

const ThumbStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${theme.spacing.sm};
`;

const ThumbItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing["2xs"]};
`;

const ThumbVideo = styled.video`
  width: 100%;
  border-radius: ${theme.borderRadius.sm};
  border: ${theme.borders.base};
  background: ${theme.colors.background.tertiary};
  object-fit: cover;
`;

function formatTime(seconds) {
  const clamped = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(clamped / 60)
    .toString()
    .padStart(2, "0");
  const secs = Math.floor(clamped % 60)
    .toString()
    .padStart(2, "0");
  const tenths = Math.floor((clamped % 1) * 100)
    .toString()
    .padStart(2, "0");
  return `${mins}:${secs}.${tenths}`;
}

function SeedSliceDialog({ isOpen, seed, onClose, onConfirm, busy }) {
  const duration = seed?.duration_seconds || 15;
  const defaultRange = useMemo(() => {
    const safeDuration = Math.max(2, Math.min(duration, 15));
    return [0, safeDuration];
  }, [duration]);

  const [range, setRange] = useState(defaultRange);

  useEffect(() => {
    if (isOpen) {
      setRange(defaultRange);
    }
  }, [isOpen, defaultRange]);

  const startPreviewRef = useRef(null);
  const endPreviewRef = useRef(null);

  const [start, end] = range;
  const clipDuration = Math.max(0, end - start);
  const confirmDisabled = clipDuration < 1 || busy;

  useEffect(() => {
    const applyTime = (videoEl, t) => {
      if (videoEl && !Number.isNaN(t)) {
        try {
          videoEl.currentTime = Math.max(0, t);
        } catch (e) {
          // ignore seek errors
        }
      }
    };
    applyTime(startPreviewRef.current, start);
    applyTime(endPreviewRef.current, end);
  }, [start, end, isOpen]);

  if (!isOpen || !seed) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={busy ? undefined : onClose} title="Choose slice">
      <Layout>
        <ThumbStrip>
          <ThumbItem>
            <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
              Start frame
            </span>
            <ThumbVideo
              ref={startPreviewRef}
              src={seed.video_url}
              preload="metadata"
              muted
              playsInline
              onLoadedMetadata={(e) => {
                try {
                  e.target.currentTime = Math.max(0, start);
                } catch (err) {
                  // ignore
                }
              }}
            />
          </ThumbItem>
          <ThumbItem>
            <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
              End frame
            </span>
            <ThumbVideo
              ref={endPreviewRef}
              src={seed.video_url}
              preload="metadata"
              muted
              playsInline
              onLoadedMetadata={(e) => {
                try {
                  e.target.currentTime = Math.max(0, end);
                } catch (err) {
                  // ignore
                }
              }}
            />
          </ThumbItem>
        </ThumbStrip>

        <div>
          <SliderWrapper>
            <Slider
              range
              min={0}
              max={Math.max(duration, 2)}
              step={0.1}
              value={range}
              allowCross={false}
              onChange={(value) => {
                if (Array.isArray(value)) {
                  setRange(value);
                }
              }}
              trackStyle={[{ backgroundColor: theme.colors.accent.primary }]}
              handleStyle={[
                {
                  borderColor: theme.colors.accent.primary,
                  backgroundColor: theme.colors.background.canvas,
                },
                {
                  borderColor: theme.colors.accent.primary,
                  backgroundColor: theme.colors.background.canvas,
                },
              ]}
            />
          </SliderWrapper>
          <TimeLabels>
            <span>Start: {formatTime(start)}</span>
            <span>End: {formatTime(end)}</span>
            <span>Duration: {clipDuration.toFixed(1)}s</span>
          </TimeLabels>
        </div>

        <Actions>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onConfirm({ start_seconds: Number(start.toFixed(2)), end_seconds: Number(end.toFixed(2)) })
            }
            disabled={confirmDisabled}
          >
            {busy ? "Creating..." : "Create project"}
          </Button>
        </Actions>
      </Layout>
    </Modal>
  );
}

export default SeedSliceDialog;
